import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreatePlanDto, QueryPlansDto, UpdatePlanDto } from '@control-center/shared';

import {
  createPlan,
  deletePlan,
  fetchPlan,
  fetchPlans,
  hardDeletePlan,
  updatePlan,
  type PlanDetail,
} from '../lib/plans-api';

export const plansKeys = {
  all: ['plans'] as const,
  lists: () => [...plansKeys.all, 'list'] as const,
  list: (filters: QueryPlansDto) => [...plansKeys.lists(), filters] as const,
  detail: (id: string) => [...plansKeys.all, 'detail', id] as const,
};

export function usePlans(filters: QueryPlansDto = { limit: 50 }) {
  return useQuery({
    queryKey: plansKeys.list(filters),
    queryFn: () => fetchPlans(filters),
    staleTime: 30_000,
  });
}

export function usePlan(id: string | undefined) {
  return useQuery<PlanDetail>({
    queryKey: plansKeys.detail(id ?? ''),
    queryFn: () => fetchPlan(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlanDto) => createPlan(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKeys.all });
    },
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePlanDto }) =>
      updatePlan(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: plansKeys.all });
      qc.invalidateQueries({ queryKey: plansKeys.detail(vars.id) });
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => deletePlan(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKeys.all });
    },
  });
}

export function useHardDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => hardDeletePlan(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKeys.all });
    },
  });
}
